import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

export interface GalleryImage {
  id: number
  src: string
  alt: string
  title?: string
  googledrivelink: string
}

export interface GalleryAlbum {
  id: number
  title: string
  googledrivelink: string
  coverImage: string
  images: GalleryImage[]
}

interface GalleryProps {
  albums?: GalleryAlbum[]
  images?: GalleryImage[]
  title?: string
}

const Gallery = ({ albums, images, title = 'Gallery' }: GalleryProps) => {
  
  const [selectedAlbum, setSelectedAlbum] = useState<GalleryAlbum | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const currentImages = selectedAlbum ? selectedAlbum.images : (images || [])
  
  const openLightbox = (index: number) => {
    setActiveIndex(index)
    setIsOpen(true)
  }

  const closeLightbox = () => setIsOpen(false)

  const goToPrevious = () => {
    setActiveIndex((prev) => (prev - 1 + currentImages.length) % currentImages.length)
  }

  const goToNext = () => {
    setActiveIndex((prev) => (prev + 1) % currentImages.length)
  }

  const handleAlbumClick = (album: GalleryAlbum) => {
    setSelectedAlbum(album)
  }

  const handleBackToAlbums = () => {
    setSelectedAlbum(null)
  }

  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') goToNext()
      if (e.key === 'ArrowLeft') goToPrevious()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, currentImages.length])

  // Show album view (3 albums)
  if (!selectedAlbum && albums) {
    return (
      <section className="bg-gray-800 py-12 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-primary-light text-sm md:text-base font-semibold uppercase tracking-wider">
                Gallery
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4 mb-6">
                {title}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {albums.slice(0, 3).map((album) => (
                <div
                  key={album.id}
                  className="relative group overflow-hidden rounded-lg aspect-square cursor-pointer bg-gray-700"
                  onClick={() => handleAlbumClick(album)}
                >
                  <img
                    src={album.coverImage}
                    alt={album.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex items-end p-6">
                    <div className="w-full">
                      <h3 className="text-primary-light font-bold text-xl mb-2">{album.title}</h3>
                      <p className="text-gray-300 text-sm">{album.images.length} photos</p>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 bg-primary-light/90 backdrop-blur-sm rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/gallery"
                className="inline-block bg-primary-light hover:bg-primary-dark text-white px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                View Full Gallery
              </Link>
            </div>
          </div>
        </div>
      </section>
    )
  }

  // Show images in selected album
  return (
    <section className="bg-gray-800 py-12 md:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-primary-light text-sm md:text-base font-semibold uppercase tracking-wider">
              Gallery
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4 mb-6">
              {selectedAlbum ? selectedAlbum.title : title}
            </h2>
          </div>

          {selectedAlbum && (
            <div className="mb-8">
              <button
                onClick={handleBackToAlbums}
                className="inline-flex items-center text-primary-light hover:text-primary-dark font-semibold transition-colors mb-6"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Albums
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {currentImages.map((image, index) => (
              <div
                key={image.id}
                className="relative group overflow-hidden rounded-lg aspect-square cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                {image.title && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <p className="text-white font-semibold text-sm">{image.title}</p>
                  </div>
                )}

              </div>
            ))}
          </div>

          {selectedAlbum?.googledrivelink != null &&
            
              <div className="inset-0 flex items-center justify-center mt-8">
                <a
                  href={selectedAlbum?.googledrivelink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer bg-primary-light hover:bg-primary-dark text-white px-4 py-3 rounded-lg font-semibold transition-colors text-sm md:text-base inline-block text-center"
                >
                  View More
                </a>
              </div>
          }

           

          {!selectedAlbum && (
            <div className="text-center mt-8">
              <Link
                to="/gallery"
                className="inline-block bg-primary-light hover:bg-primary-dark text-white px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                View Full Gallery
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {isOpen && currentImages[activeIndex] && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="absolute inset-0" onClick={closeLightbox}></div>

          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white bg-black/60 hover:bg-black/80 p-3 rounded-full z-20"
            aria-label="Close gallery"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {currentImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevious()
                }}
                className="absolute left-6 sm:left-10 text-white bg-black/60 hover:bg-black/80 p-3 rounded-full z-20"
                aria-label="Previous image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  goToNext()
                }}
                className="absolute right-6 sm:right-10 text-white bg-black/60 hover:bg-black/80 p-3 rounded-full z-20"
                aria-label="Next image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          <div className="relative max-w-5xl w-full z-10">
            <div className="relative overflow-hidden rounded-xl shadow-2xl bg-black/40">
              <img
                src={currentImages[activeIndex].src}
                alt={currentImages[activeIndex].alt}
                className="w-full max-h-[80vh] object-contain"
              />
              {(currentImages[activeIndex].title || currentImages[activeIndex].alt) && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
                  <p className="text-white text-lg font-semibold">
                    {currentImages[activeIndex].title || currentImages[activeIndex].alt}
                  </p>
                </div>
              )}
            </div>

            {currentImages.length > 1 && (
              <div className="flex justify-center space-x-2 mt-4">
                {currentImages.map((img, index) => (
                  <button
                    key={img.id}
                    onClick={() => setActiveIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === activeIndex ? 'w-8 bg-primary-light' : 'w-2 bg-white/60 hover:bg-white'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

export default Gallery

